/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { type JSX } from 'react';

import { useGateValue } from '@coursera-web/statsig';

import { getMarketingStatusTagForSlug } from 'bundles/product-card/components/utils/marketingTagUtils';
import NewAiBadge from 'bundles/unified-description-page-common/components/shared/badges/NewAiBadge';
import TopAiProgramBadge from 'bundles/unified-description-page-common/components/shared/badges/TopAiProgramBadge';
import TrendingRightNowBadge from 'bundles/unified-description-page-common/components/shared/badges/TrendingRightNowBadge';
import TopInstructorBadge from 'bundles/unified-description-page-common/components/shared/instructors/TopInstructorBadge';
import { shouldShowNewAiSkillsBadge } from 'bundles/unified-description-page-common/utils/shouldShowNewAiSkillsBadge';

const MAX_HERO_PILLS = 3; // new hard cap on max pills shown

const styles = {
  container: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--cds-spacing-150);
    flex-wrap: wrap;
  `,
};

type Props = {
  slug?: string;
  isTopInstructor?: boolean;
};

const HeroBadges = ({ slug, isTopInstructor }: Props) => {
  const isTopAiTrendingPillsEnabled = useGateValue('xdp_top_ai_trending_pills'); // gates "Top AI program" / "Trending right now" badges

  const marketingStatusTag = isTopAiTrendingPillsEnabled && slug ? getMarketingStatusTagForSlug(slug) : undefined;
  const topAiProgramTag = marketingStatusTag?.category === 'top-ai-program' ? marketingStatusTag : undefined;
  const trendingNowTag = marketingStatusTag?.category === 'trending-now' ? marketingStatusTag : undefined;
  // TODO(xdp_top_ai_trending_pills): "New AI skills" pill is retired if experiment succeeds.
  // If the above is true, delete this branch, the NewAiBadge/shouldShowNewAiSkillsBadge imports above, and the gate check.
  const shouldShowNewAiSkills = !isTopAiTrendingPillsEnabled && shouldShowNewAiSkillsBadge(slug);

  const badgeList: JSX.Element[] = [];
  if (isTopInstructor) {
    badgeList.push(<TopInstructorBadge key="top-instructor" />);
  }
  if (topAiProgramTag) {
    badgeList.push(<TopAiProgramBadge key="top-ai-program" marketingTag={topAiProgramTag} />);
  } else if (shouldShowNewAiSkills) {
    badgeList.push(<NewAiBadge key="new-ai-skills" slug={slug} />);
  }
  if (trendingNowTag) {
    badgeList.push(<TrendingRightNowBadge key="trending-now" marketingTag={trendingNowTag} />);
  }
  const heroBadges = badgeList.slice(0, MAX_HERO_PILLS);

  return <div css={styles.container}>{heroBadges}</div>;
};

export default HeroBadges;
